import { useQuery } from "@tanstack/react-query";
import {
  Settings,
  Car,
  Clock,
  Home,
  ClipboardList,
  Users,
  Building2,
  FileText,
  BarChart2,
  BarChart3,
  Megaphone,
  MessageSquare,
  QrCode,
  DollarSign,
  type LucideIcon,
} from "lucide-react";
import { api } from "@/lib/api";
import { useSoloDriverMembership, useIsPaidDriver } from "@/stores/authStore";

/**
 * Shared navigation item interface for sidebar components.
 * Used by both AccountLayout and DashboardLayout for consistency.
 */
export interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  roles?: string[];
  badge?: number;
  /**
   * Show a small unread dot ON the item's icon. The numeric badge sits at the
   * row's far edge — the dot keeps "something is unread" glanceable in the
   * narrow mobile drawer where the badge can sit off-eye-line.
   */
  dot?: boolean;
}

interface UnreadCountResponse {
  success: boolean;
  data: {
    count: number;
  };
}

/**
 * Hook that provides consistent sidebar navigation configuration.
 *
 * GUARDRAIL: This is the single source of truth for sidebar navigation items.
 * Both AccountLayout and DashboardLayout MUST use this hook to ensure
 * navigation items remain consistent across all layouts.
 *
 * DO NOT define navigation items directly in layout components.
 *
 * @returns Sidebar navigation items and helper functions
 */
export function useSidebarNavigation() {
  // Solo drivers (free + paid tier) get a "Share booking link" entry pointing
  // at their own company's public booking page. Fleet-only drivers have no
  // solo company, so the entry is omitted for them.
  const soloDriverMembership = useSoloDriverMembership();
  // Driver analytics are gated to paid_driver tier (allowDriverAnalytics flag).
  const isPaidDriver = useIsPaidDriver();

  // Fetch unread notification count for sidebar badge
  const { data: unreadCount } = useQuery({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const response = await api.get<UnreadCountResponse>("/notifications/unread-count");
      return response.data.data.count;
    },
    refetchInterval: 60000, // 60s fallback; realtime invalidation drives live updates
  });

  // Fetch unread message count for the Messages badge.
  const { data: unreadMessages } = useQuery({
    queryKey: ["conversations", "unread-count"],
    queryFn: async () => {
      const response = await api.get<UnreadCountResponse>("/conversations/unread-count");
      return response.data.data.count;
    },
    refetchInterval: 60000,
  });

  /**
   * Personal section navigation items.
   * These should ALWAYS be visible regardless of which page the user is on.
   */
  const personalNavItems: NavItem[] = [
    {
      // The /account landing is a lightweight "Home" (next ride + quick actions),
      // not a trip-history "Activity" view — that word collides with the
      // Uber-rider convention where Activity = trip history (G1 resolution).
      label: "Home",
      href: "/account",
      icon: Home,
    },
    {
      // Holds upcoming, manageable rides too — not just history. Kept distinct
      // from the driver-side "My Rides" item below.
      label: "My Trips",
      href: "/account/bookings",
      icon: ClipboardList,
    },
    {
      label: "Messages",
      href: "/account/messages",
      icon: MessageSquare,
      badge: unreadMessages ?? 0,
      dot: (unreadMessages ?? 0) > 0,
    },
    {
      // Notifications is no longer a standalone sidebar item: the unread bell in
      // the layout chrome carries the count, and the full stream is reachable
      // via the bell's "view all" link (→ /account/notifications). Notification
      // *preferences* + push opt-in now live under Settings → Notifications.
      label: "Settings",
      href: "/account/settings",
      icon: Settings,
    },
  ];

  /**
   * Driver section navigation items.
   * Shown to users with driver role or pending driver applications.
   */
  const driverNavItems: NavItem[] = [
    {
      label: "My Rides",
      href: "/account/rides",
      icon: Car,
    },
    {
      label: "Availability",
      href: "/account/availability",
      icon: Clock,
    },
    {
      label: "Memberships",
      href: "/account/driver/memberships",
      icon: Building2,
    },
    {
      label: "Documents",
      href: "/account/driver/documents",
      icon: FileText,
    },
    {
      label: "My Vehicles",
      href: "/account/driver/vehicles",
      icon: Car,
    },
    // Solo drivers only: share their own public booking page (link + QR).
    ...(soloDriverMembership
      ? [
          {
            label: "Share booking link",
            href: "/account/share-booking",
            icon: QrCode,
          },
        ]
      : []),
    // Paid driver tier only: driver analytics dashboard.
    ...(isPaidDriver
      ? [
          {
            label: "Analytics",
            href: "/account/driver/analytics",
            icon: BarChart2,
          },
        ]
      : []),
  ];

  /**
   * Business section navigation items.
   * Shown to users with admin/manager roles.
   * @param companyId - The company ID to use in route paths
   * @returns Navigation items for the business section
   */
  const getBusinessNavItems = (companyId: string): NavItem[] => [
    {
      label: "Dashboard",
      href: `/${companyId}/dashboard`,
      icon: Home,
    },
    {
      label: "Reservations",
      href: `/${companyId}/dashboard/reservations`,
      icon: ClipboardList,
    },
    {
      label: "Team",
      href: `/${companyId}/dashboard/team`,
      icon: Users,
      roles: ["admin", "manager"],
    },
    {
      label: "Dispatch",
      href: `/${companyId}/dashboard/dispatch`,
      icon: Megaphone,
      roles: ["admin", "manager"],
    },
    {
      label: "Messages",
      href: `/${companyId}/dashboard/messages`,
      icon: MessageSquare,
      roles: ["admin", "manager"],
      badge: unreadMessages ?? 0,
      dot: (unreadMessages ?? 0) > 0,
    },
    {
      label: "Vehicles",
      href: `/${companyId}/dashboard/vehicles`,
      icon: Car,
      roles: ["admin", "manager"],
    },
    {
      label: "Analytics",
      href: `/${companyId}/dashboard/analytics`,
      icon: BarChart3,
      // Server-side gate is `allowAnalytics` on the tier. Free/paid driver
      // tiers don't get this; business+ tiers do. The page itself surfaces a
      // friendly message if the API returns 403, so showing the link to all
      // admin/manager roles is fine - they'll find out it's plan-gated when
      // they click. Considered: hide entirely from non-business tiers; that
      // requires plumbing the tier through here.
      roles: ["admin", "manager"],
    },
    {
      label: "Pricing",
      href: `/${companyId}/dashboard/pricing`,
      icon: DollarSign,
      roles: ["admin", "manager"],
    },
    {
      label: "Settings",
      href: `/${companyId}/dashboard/settings`,
      icon: Settings,
      roles: ["admin"],
    },
  ];

  return {
    personalNavItems,
    driverNavItems,
    getBusinessNavItems,
    unreadNotificationCount: unreadCount ?? 0,
  };
}
